import { dateToYMDformat, findClosestRecord, getContractEndDate, getContractRange, getRangeFromDates } from 'utils/date'
import { getDefaultWindow, isShortTermCapable, WINDOW_ERROR_TYPES } from 'utils/shortTerm'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { ACCEPTED_METHODS } from 'payments/constants'
import { STATUS } from 'utils/constants'
import { POST } from 'rest/request'
import * as urls from 'rest/urls'
import { v7 } from 'uuid'

export const BASKET_TYPES = {
  rent: 'rent',
  shop: 'shop',
  rentAddon: 'rentAddon'
}

export const DELIVERY_ACTIONS = {
  add: 'add',
  remove: 'remove',
  moveToTheBeginning: 'moveToTheBeginning',
  edit: 'edit'
}

const basketItem = {
  type: BASKET_TYPES.rent,
  object: 'Warehouse',
  start: '',
  hash: '',
  end: '',
  name: '',
  amount: 1,
  placeId: '',
  dailyPrice: '',
  monthlyPrice: '',
  placeAddress: '',
  hasOnlineAccess: false,
  periodsToPay: 0,
  noticePeriod: 0,
}

const initialState = {
  cartId: '',
  invoice: false,
  unlock: false,
  // backup: false,
  time: 0,
  lastSentTime: 0,
  errors: null,
  paymentInProgress: false,
  terms: {
    rentalTerms: false,
    privacyPolicy: false,
    termsAndConditions: false,
  },
  discountCode: '',
  selectedWarehouses: [],
  checkPrice: {},
  list: [],
  delivery: [],
  toPay: {},
  // Błędy okna najmu krótkoterminowego (§2.5) per identifier — pokazywane przy wyborze terminu, nie w ReservationFailedPopup
  windowErrors: {},
  // Dane klienta i stan walidacji do umowy — z odpowiedzi POST /cart i POST /cart/customer (nie są zapisywane w storage)
  customer: null,
  customerCompany: null,
  customerValidation: null,
}

const applyCustomerData = (state, payload) => {
  if (payload && typeof payload === 'object' && 'customerValidation' in payload) {
    state.customer = payload.customer || null
    state.customerCompany = payload.customerCompany || null
    state.customerValidation = payload.customerValidation || null
  }
}

/**
 * Czy podpis grupy umów jest WAŻNY (docs: kontrakt dla frontu §2).
 * `signatureValid` = isSigned && !requiresNewSignature — samo `isSigned` / `signature.name` nie wystarcza,
 * bo po zmianie koszyka podpis fizycznie istnieje, ale umowa jest nieważna.
 */
export const isContractGroupSigned = (group) => {
  if (!group) return false
  if (typeof group.signatureValid === 'boolean') return group.signatureValid
  const isSigned = typeof group.isSigned === 'boolean' ? group.isSigned : !!group.signature?.name
  return isSigned && !group.requiresNewSignature
}

/**
 * Grupa podpisana, ale treść umowy / dane klienta zmieniły się po podpisie → wymaga nowego podpisu (§2a)
 */
export const contractGroupRequiresNewSignature = (group) => !!group?.isSigned && !!group?.requiresNewSignature

/**
 * Niezweryfikowany telefon klienta, którego klient może sam zmienić (brak wynajętych magazynów).
 * Ten sam warunek decyduje o zmianie numeru w SignAgreementPopup i o podpisie SMS w podsumowaniu.
 */
export const isPhoneChangeAllowed = (customer, user) =>
  !!customer && !customer.phone_verified_at && !(Number(user?.rentedWarehousesCount) > 0)

/**
 * Czy koszyk wymaga uzupełnienia danych klienta / firmy klienta przed umową
 */
export const isCustomerDataIncomplete = (customerValidation) => {
  if (!customerValidation || customerValidation.valid !== false) return false
  return customerValidation.user?.valid === false || customerValidation.company?.valid === false
}

const THREE_MINUTES = 1000 * 60 * 3
const THIRTY_MINUTES = THREE_MINUTES * 10

export const fetchInvoiceStatus = createAsyncThunk(
  'basket/fetchInvoiceStatus',
  async (_, thunkAPI) => {
    const response = await POST(`${urls.cart}/${thunkAPI.getState().basket.cartId}/status`)
    return response
  },
  {
    condition: (_, { getState }) => {
      const state = getState()
      return !!state.basket.cartId
    }
  }
)

export const getCheckPrice = createAsyncThunk(
  'basket/getCheckPrice',
  /**
   * @param {{ id?: string, companyId?: string }} user
   */
  async (user, thunkAPI) => {
    const state = thunkAPI.getState()
    const userID = state.basket?.tenant?.user?.id || user?.id || state.login.user?.id || ''
    const companyID = state.basket?.tenant?.company?.id || user?.companyId
    const selectedWarehouses = state.basket?.selectedWarehouses || []
    const body = {
      userID,
      companyID,
      objects: selectedWarehouses.map(({ placeId, hash, id, cartId, rentableType }) => {
        const object = {
          object: 'Warehouse',
          placeID: placeId,
          rentableType,
          cartId
        }
        if (id) object.id = id
        else object.hash = hash
        return object
      })
    }
    const response = await POST(urls.checkPrice, { body })
    return response.objects ? response.objects : response
  },
  {
    condition: (_, { getState }) => {
      const state = getState()
      return Array.isArray(state.basket?.selectedWarehouses) && state.basket.selectedWarehouses.length > 0
    }
  }
)

export const sendBasket = createAsyncThunk(
  'basket/send',
  /**
   * @param {{ step?: string, discountCode?: string, payments?: Object[] }} args
   */
  async (args, thunkAPI) => {
    const store = thunkAPI.getState()
    const { cartId, lastSentStep, canEdit, warehouseAccess, contractConfirmation, tenant, list, invoice, discountCode, newDocuments, delivery, time, toPay } = store.basket
    const step = args?.step || lastSentStep || 'rentingSummary'
    const body = {
      cartId,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      clientId: tenant?.user?.id,
      companyId: tenant?.company?.id || invoice?.id || '',
      canEdit,
      warehouseAccess,
      contractConfirmation,
      time: time + (canEdit === undefined ? THREE_MINUTES : THIRTY_MINUTES),
      delivery: Array.isArray(delivery)
        ? delivery.reduce((r, d) => {
          r[d.id] = d
          return r
        }, {})
        : {},
      list: list.map(({ identifier, id, name, notify, object, type, hash, placeId, insurance, pricelistId, start, end, periodsToPay, placeAddress, objectId, description, deliveryId, related, ownerId, reservation, dailyPrice, monthlyPrice, equipment, discounts, rentableType, isShortTerm, startDateTime, endDateTime, durationMinutes }) => {
        const item = (() => {
          switch (type) {
            case BASKET_TYPES.rent: {
              // tryb krótkoterminowy (§2.1): okno najmu z godzinami zamiast startDate/endDate
              const period = isShortTerm
                ? endDateTime
                  ? { startDateTime, endDateTime }
                  : { startDateTime, durationMinutes }
                : { startDate: start, endDate: end }
              return {
                identifier,
                type,
                object,
                id,
                parameters: {
                  name,
                  hash,
                  placeId,
                  pricelistId,
                  reservation,
                  insurance,
                  ...period,
                  equipment: equipment ? Object.values(equipment) : undefined,
                  periodsToPay,
                  placeAddress,
                  rentableType,
                  monthlyPrice,
                  dailyPrice,
                  discounts
                }
              }
            }

            case BASKET_TYPES.rentAddon: {
              return {
                identifier,
                type,
                id: id || null,
                object: 'LockUserOpeningMethod',
                parameters: {
                  name,
                  placeId: placeId || undefined,
                  object,
                  objectId,
                  description,
                  deliveryId,
                  related,
                  ownerId
                }
              }
            }

            case BASKET_TYPES.shop: {
              return {
                identifier,
                type,
                id,
                object,
                parameters: {
                  name,
                  description,
                  deliveryId,
                  related
                }
              }
            }

            default: return
          }
        })()
        if (item && notify) item.parameters.notify = notify
        return item
      })
    }

    // Wariant bez ekranu podpisywania umowy: akceptacja umowy w podsumowaniu (checkbox albo podpis SMS).
    // Flagę wysyłamy tylko po akceptacji - false nie wysyłamy.
    if (!store.app?.config?.contractSigningScreenEnabled && store.basket.terms?.rentalTerms && list.some(item => item.type === BASKET_TYPES.rent)) {
      body.contract_accepted = true
    }

    if (args?.discountCode || discountCode) {
      body.discountCode = args?.discountCode || discountCode
    }

    if (toPay) {
      const hashes = getSelectedHashes(store.basket)
      if (hashes.length > 0) {
        body.selectedHashes = hashes
      }
    }

    if (step) {
      body.step = step

      // Metody wymagające jawnej prośby (dziś: saved_method). Import prosto z modułu
      // stałych, a nie z barrela 'payments' - barrel ciągnie za sobą store i zrobiłby cykl.
      if (step === 'checkout' && ACCEPTED_METHODS.length > 0) {
        body.acceptedMethods = ACCEPTED_METHODS
      }

      if (step === 'finalizeBankTransfer' || step === 'contract' || step === 'newDocument' || step === 'finalize') {
        const hashes = getSelectedHashes(store.basket)
        const rentItems = list.filter(item => item.type === BASKET_TYPES.rent)
        // najem < 1 mies. płatny z góry w całości — jedyna opcja "full" (§2.4)
        const onlyShortTerm = rentItems.length > 0 && rentItems.every(item => item.isShortTerm)
        const newDocument = {
          periods_to_pay: hashes.length === 0 ? (onlyShortTerm ? 'full' : '1m') : hashes,
          type: 'invoice',
          payments: args?.payments
        }
        if (step === 'finalize' && args?.payments?.[0]?.key === 'bank_transfer') {
          body.bankTransferInitiated = 1
        }
        if (step === 'finalize' && newDocuments) {
          newDocument.invoiceIds = newDocuments.map(d => d.id)
        }

        body.newDocument = newDocument
      }
    }

    return await POST(urls.cart, { body })
  },
  {
    condition: (args, { getState }) => {
      const state = getState()
      const cartIsValid = !!state.basket.cartId && state.basket.list.length > 0
      const secondRequest = !args?.step && state.basket.isLoadingNextStep
      return cartIsValid && !secondRequest
    }
  })

let pendingSync = null

/**
 * Synchronizacja koszyka bez kroku (POST /cart) ze współdzielonym zapytaniem w locie:
 * jeśli taka synchronizacja już trwa, zwraca jej promise zamiast wysyłać koszyk drugi raz.
 */
export const syncBasket = () => (dispatch) => {
  if (!pendingSync) {
    pendingSync = Promise.resolve(dispatch(sendBasket())).finally(() => {
      pendingSync = null
    })
  }
  return pendingSync
}

const getSelectedHashes = (basket) => {
  const { list, toPay } = basket
  return list.reduce((r, { type, identifier, periodsToPay }) => {
    if (type === BASKET_TYPES.rent) {
      const periods_to_pay = toPay?.[identifier]?.periods_to_pay
      const hashes = periods_to_pay?.[periodsToPay]?.hashes
      if (hashes) {
        r.push(...hashes)
      }
    }
    return r
  }, [])
}

/**
 * Błąd okna najmu krótkoterminowego z odpowiedzi pozycji (result: "error", §2.5).
 * Źródłem prawdy jest validationErrors[].type: date | window_unavailable | pricelist; availability przychodzi
 * tylko przy window_unavailable. Inne typy (np. warehouse — obiekt zajęty, stara ścieżka) zwracają undefined
 * i idą do ogólnego ReservationFailedPopup.
 */
const getWindowError = (position) => {
  const validationErrors = Array.isArray(position?.validationErrors) ? position.validationErrors : []
  const types = validationErrors.map(e => e?.type).filter(type => WINDOW_ERROR_TYPES.includes(type))
  if (types.length === 0) return
  const errors = (Array.isArray(position?.errors) ? position.errors : position?.errors ? Object.values(position.errors).flat() : [])
    .filter(e => typeof e === 'string')
  return {
    types,
    errors,
    availability: types.includes('window_unavailable') ? position?.availability : undefined
  }
}

const getNoticePeriodFromContract = (contract) => {
  const number = Number(contract?.contractUnitNumber)
  if (!Number.isNaN(number)) {
    return number * (contract?.dateUnit === 'm' ? 30 : 1)
  }
}

export const basketSlice = createSlice({
  name: 'basket',
  initialState,
  reducers: {
    updatePaymentInProgress: (state, action) => {
      if (!action.payload) {
        state.time = Date.now()
      }
      state.paymentInProgress = action.payload
    },
    addItemToBasket: (state, action) => {
      const { payload } = action
      if (typeof payload !== 'object') return
      if (state.list.length === 0) {
        state.cartId = v7()
      }
      const newBasketItem = { identifier: v7(), ...basketItem, ...payload }
      if (!state.list.some(item => item.identifier === newBasketItem.identifier)) {
        state.list.push(newBasketItem)
        if (newBasketItem?.type === BASKET_TYPES.rent) {
          state.terms.rentalTerms = false
          const currentIdentifiers = state.list.reduce((r, i) => {
            if (i.type === BASKET_TYPES.rent) r.push(i.identifier)
            return r
          }, [])
          state.selectedWarehouses = [
            ...state.selectedWarehouses.filter(({ cartId }) => currentIdentifiers.includes(cartId)),
            { placeId: newBasketItem.placeId, id: newBasketItem.id, hash: newBasketItem.hash, cartId: newBasketItem.identifier, rentableType: newBasketItem.rentableType }
          ]
        } else if (payload?.type === BASKET_TYPES.shop) {
          state.time = Date.now()
        }
      }
    },
    removeItemFromBasket: (state, action) => {
      const removeIdentifiers = action.payload?.removeIdentifiers
      const { list } = state

      const isWarehouseRemoved = removeIdentifiers
        ? list.some(l => {
          if (l.type !== BASKET_TYPES.rent) return false
          return removeIdentifiers.includes(l.identifier)
        }) : true

      const initialList = removeIdentifiers ? list.filter(e => !removeIdentifiers.includes(e.identifier)) : undefined
      const rentList = initialList ? initialList.filter(e => e.type === BASKET_TYPES.rent) : undefined
      const newList = initialList
        ? initialList.filter(({ type, ownerId, related, placeId }) => {
          if (type === BASKET_TYPES.rentAddon) {
            return (ownerId
              ? rentList.find(e => e.identifier === ownerId)
              : related
                ? (related.every(r => r.object === 'Place' ? rentList.some(e => e.placeId === r.id) : rentList.some(e => e.identifier === r.cartId)))
                : rentList.find(e => e.placeId === placeId))
          } else {
            return true
          }
        })
        : []

      if (newList.length === 0) {
        return {
          ...initialState,
          backup: isWarehouseRemoved ? { ...state } : false,
          unlock: false
        }

      } else if (isWarehouseRemoved) {
        state.backup = { list }
        state.unlock = false
      }

      state.list = newList
    },
    updateTerms: (state, action) => {
      state.terms = action.payload
    },
    updateDelivery: (state, action) => {
      state.delivery = action.payload
    },
    updateDiscountCode: (state, action) => {
      state.discountCode = action.payload
      if (!action.payload) {
        delete state.discount
      }
    },
    updateBasketItem: (state, action) => {
      const { list } = state
      const { identifier, values } = action.payload

      if (identifier && typeof values === 'object') {
        state.terms.rentalTerms = false
        state.list = list.map(e => identifier === e.identifier ? { ...e, ...values } : e)
        if (state.windowErrors?.[identifier]) {
          delete state.windowErrors[identifier]
        }
      }
    },
    updateBasketList: (state, action) => {
      const { payload } = action
      const { list } = state

      if (typeof payload === 'object') {
        state.list = list.map(e => payload[e.identifier] ? { ...e, ...payload[e.identifier] } : e)
      }
    },
    extendFormToMap: (state, action) => { state.unlock = action.payload },
    updateCompany: (state, action) => { state.invoice = action.payload },
    updateCustomerData: (state, action) => { applyCustomerData(state, action.payload) },
    contractGroupSigned: (state, action) => {
      const { id, replacedBy, signature } = action.payload || {}
      if (!Array.isArray(state.contractGroups) || !id) return
      state.contractGroups = state.contractGroups.map(group => group.id === id || (replacedBy && group.id === replacedBy)
        ? {
          ...group,
          id: replacedBy || group.id,
          signature,
          isSigned: true,
          requiresNewSignature: false,
          signatureValid: true,
          status: 'accepted',
          awaitingSignature: false
        }
        : group)
    },
    resetTime: (state) => { state.time = Date.now() },
    restoreBackup: (state, action) => {
      const { payload } = action
      const { backup } = state

      if (payload && backup) {
        return {
          ...state,
          ...backup,
          backup: false,
          lastSentTime: 0,
          time: Date.now()
        }

      } else {
        state.backup = false
        state.lastSentTime = 0
      }
    },
    reconstructBasket: (state, action) => {
      const payload = action.payload.payload
      if (!payload || !payload.cartId || !payload.list) {
        return
      }

      const generateParametersData = (type, parameters) => {
        switch (type) {
          case BASKET_TYPES.rent: {
            const { startDate, endDate, equipment, contract, ...rest } = parameters
            return {
              ...rest,
              isShortTerm: !!parameters.startDateTime,
              start: startDate,
              end: endDate,
              noticePeriod: getNoticePeriodFromContract(contract),
              rangeDate: endDate ? getRangeFromDates(endDate, startDate) : { m: -1, d: 0 },
              equipment: Array.isArray(equipment)
                ? equipment.reduce((r, e) => {
                  r[e.id] = e
                  return r
                }, {})
                : undefined
            }
          }

          case BASKET_TYPES.rentAddon:
          case BASKET_TYPES.shop: return parameters

          default: return {}
        }
      }

      const selectedWarehouses = []
      const list = payload.list.map(item => {
        const { parameters, ...rest } = item
        const parametersData = generateParametersData(rest.type, parameters)
        if (rest.type === BASKET_TYPES.rent) {
          selectedWarehouses.push({
            placeId: parametersData.placeId,
            id: rest.id,
            hash: parametersData.hash,
            cartId: rest.identifier,
            rentableType: parametersData.rentableType
          })
        }
        return {
          ...rest,
          ...parametersData
        }
      })

      const delivery = payload.delivery && typeof payload.delivery === 'object'
        ? Object.values(payload.delivery)
        : []

      return {
        cartId: payload.cartId,
        invoice: false,
        unlock: false,
        backup: false,
        time: Date.now(),
        lastSentTime: 0,
        errors: false,
        paymentInProgress: false,
        isPaid: action.payload.isPaid,
        isFinalized: action.payload.isFinalized,
        discountCode: payload.discountCode || '',
        selectedWarehouses,
        checkPrice: {},
        windowErrors: {},
        list,
        delivery,
        canEdit: payload.canEdit,
        warehouseAccess: payload.warehouseAccess,
        contractConfirmation: payload.contractConfirmation,
        selectedPaymentKey: action.payload.selectedPaymentKey,
        contractGroups: action.payload.contractgroups,
        contractsSigning: action.payload.contractsSigning,
        rentingSummary: action.payload?.response?.rentingSummary || {},
        toPay: action.payload?.response?.positions || {},
        contracts: action.payload?.response?.contracts,
        terms: {
          rentalTerms: false,
          privacyPolicy: false,
          termsAndConditions: false,
        },
      }
    },
    updateTenantData: (state, action) => {
      state.tenant = action.payload
    },
    finalizeBasket: (state, action) => {
      return action.payload
        ? {
          ...initialState,
          contracts: action.payload,
          tenant: state.tenant,
          initImage: {
            type: state.list[0]?.rentableType,
            src: state.list[0]?.image
          }
        } : {
          ...initialState
        }
    },
    finalizeBasketManually: (state) => {
      return {
        ...initialState,
        contracts: state.contracts,
        tenant: state.tenant,
        initImage: {
          type: state.list[0]?.rentableType,
          src: state.list[0]?.image
        }
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchInvoiceStatus.fulfilled, (state, action) => {
        if (action.payload?.status === 'INVOICE_READY' || action.payload?.status === 'INVOICE_FAILURE') {
          state.bankTransfer = {
            ...state.bankTransfer,
            ...action.payload
          }
        }
      })
      .addCase(getCheckPrice.fulfilled, (state, action) => {
        if (!action.payload) return
        const checkPrice = {}
        for (const key in action.payload) {
          const { pricelists, pricelistId, equipment, contract_templates, ...rest } = action.payload[key]
          const priceList = pricelists?.[pricelistId]?.slider

          if (priceList && !rest.calendar?.end) {
            const lastElement = priceList.at(-1)
            priceList[priceList.length - 1] = { ...lastElement, v: '∞', m: -1, r: 100 }
          }

          checkPrice[key] = {
            ...rest,
            equipment,
            priceList,
            pricelistId,
            noticePeriod: getNoticePeriodFromContract(contract_templates?.find(contract => contract.selected)),
            status: STATUS.succeed
          }
        }

        state.list = state.list.map((item) => {
          if (item.type === BASKET_TYPES.rent && !item.ok && checkPrice?.[item.identifier] && isShortTermCapable(checkPrice[item.identifier].shortTerm)) {
            // tryb krótkoterminowy (§1): bez suwaka miesięcznego — domyślnie najbliższy wolny termin
            const { shortTerm, pricelistId, equipment, calendar, placeAddress, placeGps, size } = checkPrice[item.identifier]
            const defaultWindow = item.startDateTime ? undefined : getDefaultWindow(shortTerm, calendar)
            const startDateTime = item.startDateTime || defaultWindow?.startDateTime
            return {
              ...item,
              // bez startu pozycja poszłaby ścieżką miesięczną — koszyk wysyłamy dopiero po wyborze terminu
              ok: !!startDateTime,
              isShortTerm: true,
              start: undefined,
              end: undefined,
              startDateTime,
              durationMinutes: item.endDateTime ? undefined : (item.durationMinutes || defaultWindow?.durationMinutes),
              size,
              placeGps,
              pricelistId,
              placeAddress,
              equipment: item.equipment || equipment?.reduce((r, equipment) => {
                if (equipment.quantity > 0) {
                  r[equipment.id] = equipment
                }
                return r
              }, {}) || {},
            }
          } else if (item.type === BASKET_TYPES.rent && !item.ok && checkPrice?.[item.identifier] && checkPrice[item.identifier].priceList) {
            const { priceList, pricelistId, equipment, calendar, placeAddress, placeGps, size, noticePeriod, rentalMonthMinusOneDay } = checkPrice[item.identifier]
            const minusOneDay = !!rentalMonthMinusOneDay
            const lastPriceListItem = priceList.at(-1)
            const start = item.start || calendar?.start || dateToYMDformat()
            const end = item.end || calendar?.end || getContractEndDate((item.rangeDate || lastPriceListItem), start, minusOneDay)
            // przy rentalMonthMinusOneDay okres liczymy z end + 1 — też dla koszyka odtworzonego w reconstructBasket (tam flaga nie jest jeszcze znana)
            const rangeDate = (minusOneDay && item.end)
              ? getContractRange(item.end, start, true)
              : item.rangeDate || (end ? getContractRange(end, start, minusOneDay) : { m: -1, d: 0 })
            const priceListItem = rangeDate.m === -1
              ? lastPriceListItem
              : findClosestRecord(priceList, rangeDate)
            return {
              ...item,
              ok: true,
              start,
              end,
              size,
              placeGps,
              rangeDate,
              pricelistId,
              placeAddress,
              noticePeriod: item.noticePeriod || noticePeriod,
              isShortTerm: false,
              dailyPrice: item.dailyPrice || priceListItem?.gross_price_d,
              monthlyPrice: item.monthlyPrice || priceListItem?.gross_price,
              equipment: item.equipment || equipment?.reduce((r, equipment) => {
                if (equipment.quantity > 0) {
                  r[equipment.id] = equipment
                }
                return r
              }, {}) || {},
            }
          } else {
            return item
          }
        })

        state.time = Date.now()
        state.checkPrice = checkPrice
      })
      .addCase(sendBasket.pending, (state, action) => {
        const toPay = state.toPay
        for (const key in toPay) {
          const { to_pay, to_pay_monthly, to_pay_without_addons, object_price, ...rest } = toPay[key]
          toPay[key] = rest
        }
        if (action.meta.arg?.step) {
          state.isLoadingNextStep = true
        }
        state.toPay = toPay
      })
      .addCase(sendBasket.fulfilled, (state, action) => {
        const { positions: positionsObj, checkout, rentingSummary, newDocuments, sold, isFinished, contracts = [], discount, locked, contractgroups, contractsSigning, bankTransfer, contractTemplateUrl } = action.payload
        const { list, tenant, canEdit } = state

        if (locked && !sold) {
          state.paymentInProgress = true
          if (action.meta.arg?.step) {
            state.isLoadingNextStep = false
          }

        } else if (sold || isFinished) {
          return {
            ...initialState,
            tenant,
            contracts,
            initImage: {
              type: state.list[0]?.rentableType,
              src: state.list[0]?.image
            }
          }

        } else {
          if (action.meta.arg?.step) {
            state.isLoadingNextStep = false
          }

          applyCustomerData(state, action.payload)

          const positionsList = Object.entries(positionsObj || {})
          const newList = positionsList.reduce((r, [k, v]) => {
            if (v.result === 'ok') {
              r.toPay = {
                ...r.toPay,
                [k]: v
              }

            } else if (v.result === 'reservation_required') {
              r.reservations = {
                ...(r.reservations || {}),
                [k]: v?.reservation
              }

            } else if (v.result === 'error') {
              const item = list.find(({ identifier }) => identifier === k)
              const windowError = item?.isShortTerm ? getWindowError(v) : undefined
              if (windowError) {
                // zły / zajęty termin — klient poprawia go przy wyborze terminu (§2.5)
                r.windowErrors[k] = windowError
              } else if (item) {
                r.errors.push({ ...item, result: v.result, errors: v.errors })
              }
            }
            return r
          }, { toPay: {}, reservations: undefined, errors: [], windowErrors: {} })

          state.toPay = newList.toPay
          state.windowErrors = newList.windowErrors
          state.discount = discount
          state.errors = newList.errors.length > 0 ? newList.errors : false
          state.lastSentTime = state.time + (canEdit === undefined ? THREE_MINUTES : THIRTY_MINUTES) - 500
          state.lastSentStep = action.meta.arg?.step || ''
          if (newList.reservations) {
            state.list = state.list.map(item => {
              return newList.reservations[item.identifier]
                ? { ...item, reservation: newList.reservations[item.identifier] }
                : item
            })
          }
          if (contractTemplateUrl) {
            state.contractTemplateUrl = contractTemplateUrl
          }
          if (bankTransfer) {
            state.bankTransfer = bankTransfer
          }
          if (contractgroups) {
            state.contractGroups = contractgroups
          }
          if (contractsSigning) {
            state.contractsSigning = contractsSigning
          }
          if (newDocuments) {
            state.newDocuments = newDocuments
          }
          if (checkout) {
            state.checkout = checkout
          }
          if (rentingSummary) {
            state.rentingSummary = rentingSummary
          }
        }
      })
  }
})

export const { updatePaymentInProgress, addItemToBasket, removeItemFromBasket, updateDiscountCode, updateDelivery, changeSelectedItem, updateBasketList, updateBasketItem, extendFormToMap, updateTerms, updateCompany, resetTime, restoreBackup, reconstructBasket, updateTenantData, finalizeBasket, finalizeBasketManually, updateCustomerData, contractGroupSigned } = basketSlice.actions

export const selectBasket = state => state.basket
export const selectBasketTime = state => state.basket.time
export const selectBasketList = state => state.basket.list
export const selectBasketToPay = state => state.basket.toPay
export const selectBasketTerms = state => state.basket.terms
export const selectBasketIsPaid = state => state.basket.isPaid
export const selectBasketCanEdit = state => state.basket.canEdit
export const selectBasketTenantData = state => state.basket.tenant
export const selectBasketCustomer = state => state.basket.customer
export const selectBasketCustomerCompany = state => state.basket.customerCompany
export const selectBasketCustomerValidation = state => state.basket.customerValidation
export const selectBasketCheckout = state => state.basket.checkout
export const selectBasketDiscount = state => state.basket.discount
export const selectBasketInitImage = state => state.basket.initImage
export const selectBasketContracts = state => state.basket.contracts
export const selectBasketDeliveries = state => state.basket.delivery
export const selectBasketCheckPrice = state => state.basket.checkPrice
export const selectBasketIsFinalized = state => state.basket.isFinalized
export const selectBasketDiscountCode = state => state.basket.discountCode
export const selectBasketBankTransfer = state => state.basket.bankTransfer
export const selectBasketIsReservationUnlocked = state => state.basket.unlock
export const selectBasketRentingSummary = state => state.basket.rentingSummary
export const selectBasketContractGroups = state => state.basket.contractGroups
export const selectBasketContractsSigning = state => state.basket.contractsSigning
export const selectBasketPaymentInProgress = state => state.basket.paymentInProgress
export const selectBasketSelectedPaymentKey = state => state.basket.selectedPaymentKey
export const selectBasketSelectedWarehouses = state => state.basket.selectedWarehouses
export const selectBasketContractTemplateUrl = state => state.basket.contractTemplateUrl
export const selectBasketIsUnpresentedRentedWarehouses = state => Array.isArray(state.basket.contracts) && state.basket.contracts.length > 0
export const selectBasketIsEmpty = state => Array.isArray(state.basket.list) ? state.basket.list.length === 0 : true
export const selectBasketRedirect = (state, identifier) => {
  if (typeof identifier === 'string' && state.basket.list.some(item => item.identifier === identifier)) return
  return state.basket.list.find(item => item.type === BASKET_TYPES.rent)?.identifier || -1
}
export const selectBasketWindowError = (state, identifier) => state.basket.windowErrors?.[identifier]
export const selectBasketCheckPriceItem = (state, identifier) => {
  return state.basket.checkPrice?.[identifier] || {}
}
export const selectBasketItem = (state, id) => {
  const { list } = state.basket
  if (typeof id === 'number') {
    return list[id] || {}
  }

  if (typeof id === 'string') {
    const index = list.findIndex(item => item.identifier === id)
    return { ...list[index], index } || {}
  }

  const index = list.findLastIndex(item => item.type === BASKET_TYPES.rent)
  return { ...list[index], index } || {}
}

export default basketSlice.reducer
